import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Clock, Mail, FileText, Shield, MessageSquare, CheckCircle2, RefreshCw, Download } from "lucide-react";
import { Button } from "@/components/ui/button";

const ICON_MAP = {
  mail: Mail,
  file: FileText,
  shield: Shield,
  msg: MessageSquare,
  check: CheckCircle2,
  refresh: RefreshCw,
};

const COLOR_MAP = {
  email_sent: "border-blue-400 bg-blue-50 text-blue-800",
  quote_created: "border-purple-400 bg-purple-50 text-purple-800",
  quote_accepted: "border-emerald-400 bg-emerald-50 text-emerald-800",
  policy_created: "border-amber-400 bg-amber-50 text-amber-800",
  policy_renewed: "border-cyan-400 bg-cyan-50 text-cyan-800",
  message: "border-slate-400 bg-slate-50 text-slate-800",
};

export function ClientTimelineDialog({ client, open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !client?.id) return;
    setLoading(true);
    api.get(`/clients/${client.id}/timeline`)
      .then((r) => setData(r.data))
      .catch(() => setData({ events: [] }))
      .finally(() => setLoading(false));
  }, [open, client?.id]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="client-timeline-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-violet-600" />
              Cronologia · {client?.surname} {client?.name}
            </span>
            <Button
              data-testid="timeline-export-pdf"
              size="sm"
              variant="outline"
              onClick={async () => {
                try {
                  const resp = await api.get(`/clients/${client.id}/timeline/pdf`, { responseType: "blob" });
                  const url = URL.createObjectURL(resp.data);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `cronologia_${(client.surname || "cliente").toLowerCase()}.pdf`;
                  a.click();
                  URL.revokeObjectURL(url);
                } catch {/* ignore */ }
              }}
              className="text-xs gap-1.5 mr-6"
            >
              <Download className="h-3.5 w-3.5" /> Esporta PDF
            </Button>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          {loading && <p className="text-sm text-slate-400 text-center py-8">Caricamento…</p>}
          {!loading && data?.events?.length === 0 && (
            <p className="text-sm text-slate-400 text-center py-8 italic">Nessuna attività registrata per questo cliente.</p>
          )}
          {!loading && data?.events?.map((e, i) => {
            const Icon = ICON_MAP[e.icon] || Clock;
            const colorClass = COLOR_MAP[e.type] || "border-slate-300 bg-white text-slate-700";
            const dt = e.ts ? new Date(e.ts) : null;
            return (
              <div
                key={i}
                data-testid={`timeline-event-${i}`}
                className={`border-l-4 ${colorClass} rounded-r-md pl-3 py-2 pr-2 flex items-start gap-2.5`}
              >
                <Icon className="h-4 w-4 mt-0.5 shrink-0 opacity-70" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm">{e.title}</div>
                  {e.description && <div className="text-xs opacity-80 mt-0.5">{e.description}</div>}
                </div>
                <div className="text-[10px] uppercase tracking-wider opacity-60 shrink-0 tabular-nums">
                  {dt && (
                    <>
                      <div>{String(dt.getDate()).padStart(2, "0")}/{String(dt.getMonth() + 1).padStart(2, "0")}/{dt.getFullYear()}</div>
                      <div className="text-right">{String(dt.getHours()).padStart(2, "0")}:{String(dt.getMinutes()).padStart(2, "0")}</div>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ClientTimelineDialog;
